import { useEffect, useState } from "react";
import { authAxios as ax } from "@/contexts/AuthContext";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Minus, RefreshCw, Activity } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stable color per intent — Swiss brutalist palette mapped to severity intuitively
const INTENT_COLORS = {
  "Outage / Service Down": "#FF2A2A",
  "Hardware Failure": "#FFB800",
  "Configuration / Setup": "#002FA7",
  "Billing / Account": "#0A0A0A",
  "Feature Request": "#00C853",
  "General Inquiry": "#9CA3AF",
  "Security Incident / Breach Alert": "#8B0000",
  "Onboarding / System Commissioning": "#FFB800",
};

const PRIORITY_COLORS = {
  Low: "#9CA3AF",
  Medium: "#002FA7",
  High: "#FFB800",
  Urgent: "#FF2A2A",
};

function TrendBadge({ delta }) {
  if (delta === 0 || delta == null) {
    return (
      <span className="flex items-center gap-0.5 text-[10px] font-bold text-[#9CA3AF]" data-testid="trend-flat">
        <Minus size={10} /> 0%
      </span>
    );
  }
  const up = delta > 0;
  const color = up ? "text-[#FF2A2A]" : "text-[#00C853]";
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`flex items-center gap-0.5 text-[10px] font-bold ${color}`} data-testid={up ? "trend-up" : "trend-down"}>
      <Icon size={10} /> {Math.abs(delta)}%
    </span>
  );
}

export default function IntentHeatmap({ t, projectId = null, embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [windowDays, setWindowDays] = useState(30);

  const load = async (win = windowDays) => {
    setLoading(true);
    try {
      const params = { window: win };
      if (projectId) params.project_id = projectId;
      const res = await ax.get(`${API}/analytics/intent-trends`, { params });
      setData(res.data);
    } catch (e) {
      // best-effort silent
      if (process.env.NODE_ENV !== "production") console.warn("Failed to load intent trends:", e?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const onChangeWindow = (w) => { setWindowDays(w); load(w); };

  const distributionData = (data?.categories || [])
    .map((cat) => ({
      name: cat.replace(" / ", "/"),
      fullName: cat,
      count: data?.totals?.[cat] || 0,
      delta: data?.deltas?.[cat]?.delta_pct ?? 0,
      fill: INTENT_COLORS[cat] || "#9CA3AF",
    }))
    .filter((row) => row.count > 0)
    .sort((a, b) => b.count - a.count);

  const priorityData = data ? Object.entries(data.priority_totals || {}).map(([k, v]) => ({
    name: k,
    value: v,
    fill: PRIORITY_COLORS[k] || "#9CA3AF",
  })) : [];

  const totalCalls = data?.total_calls_with_intent || 0;

  return (
    <div className={embedded ? "" : "border border-[#E5E7EB] bg-white p-6"} data-testid="intent-heatmap">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#002FA7]">
            <Activity size={18} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Intent Heatmap</h3>
            <p className="text-[10px] tracking-[0.15em] uppercase text-[#9CA3AF] font-bold">
              {totalCalls} classified calls · last {data?.window_days || windowDays} days
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {[7, 14, 30].map((w) => (
            <button
              key={w}
              onClick={() => onChangeWindow(w)}
              className={`px-3 h-7 text-[10px] font-bold tracking-[0.1em] uppercase border transition-colors ${
                windowDays === w
                  ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                  : "bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#0A0A0A]"
              }`}
              data-testid={`window-${w}d`}
            >
              {w}D
            </button>
          ))}
          <Button
            size="sm"
            variant="outline"
            className="rounded-none h-7 text-[10px] font-bold tracking-[0.1em] uppercase ml-1"
            onClick={() => load()}
            disabled={loading}
            data-testid="refresh-intent"
          >
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>

      {totalCalls === 0 && !loading && (
        <div className="text-center py-10 border border-dashed border-[#E5E7EB] bg-[#F8F9FA]" data-testid="intent-empty">
          <p className="text-sm text-[#9CA3AF]">
            No classified calls yet in this window. Intents will appear here as calls complete.
          </p>
        </div>
      )}

      {totalCalls > 0 && (
        <>
          {/* Main bar chart — issue distribution */}
          <div className="mb-6">
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-2">
              Issue distribution
            </p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={distributionData} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fontWeight: 700 }}
                  angle={-30}
                  textAnchor="end"
                  height={70}
                  interval={0}
                />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: "rgba(0,47,167,0.05)" }}
                  contentStyle={{ borderRadius: 0, border: "1px solid #0A0A0A", fontSize: 11 }}
                  formatter={(value, _name, item) => [`${value} calls`, item.payload.fullName]}
                />
                <Bar dataKey="count" radius={0}>
                  {distributionData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Trend cards per intent */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-6">
            {distributionData.map((row) => (
              <div
                key={row.fullName}
                className="border border-[#E5E7EB] bg-[#F8F9FA] p-3 border-l-4"
                style={{ borderLeftColor: row.fill }}
                data-testid={`intent-card-${row.fullName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              >
                <p className="text-[9px] tracking-[0.15em] uppercase font-bold text-[#9CA3AF] mb-1 line-clamp-1">
                  {row.fullName}
                </p>
                <div className="flex items-end justify-between">
                  <p className="text-2xl font-black text-[#0A0A0A] tracking-tight">{row.count}</p>
                  <TrendBadge delta={row.delta} />
                </div>
              </div>
            ))}
          </div>

          {/* Priority distribution */}
          {priorityData.some((p) => p.value > 0) && (
            <div className="mb-6">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-2">
                Priority mix
              </p>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={priorityData} margin={{ top: 5, right: 10, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fontWeight: 700 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #0A0A0A", fontSize: 11 }} />
                  <Bar dataKey="value" radius={0}>
                    {priorityData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Project breakdown (admins / multi-project users) */}
          {(data?.project_breakdown || []).length > 1 && (
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-2">
                Top projects by call volume
              </p>
              <div className="space-y-1.5">
                {data.project_breakdown.slice(0, 5).map((p) => {
                  const top = Object.entries(p.by_intent || {})
                    .filter(([, v]) => v > 0)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 3);
                  return (
                    <div key={p.project_id} className="p-2.5 bg-[#F8F9FA] border border-[#E5E7EB]" data-testid={`project-breakdown-${p.project_id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-bold text-[#0A0A0A]">{p.project_name}</p>
                        <Badge variant="outline" className="rounded-none text-[9px] font-bold">{p.total} calls</Badge>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        {top.map(([cat, count]) => (
                          <span
                            key={cat}
                            className="text-[9px] font-bold px-1.5 py-0.5"
                            style={{ background: `${INTENT_COLORS[cat]}1A`, color: INTENT_COLORS[cat] }}
                          >
                            {cat.split(" / ")[0]}: {count}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
